import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import clsx from 'clsx'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import { Badge } from '@/components/catalyst/badge'
import { ArrowDownIcon, ArrowUpIcon, Bars3Icon, Squares2X2Icon, GlobeAltIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface ShipmentRow {
  id: number
  po_number: string | null
  incoming_ship_date?: string
  ship_out_date?: string
  warehouse: string | null
}

interface LogisticsIndexProps {
  incomingOrders: ShipmentRow[]
  outgoingOrders: ShipmentRow[]
}

// ─── Helpers ────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  if (!d) return '—'
  // Already MM/DD/YYYY from backend
  return d
}

// ─── Component ──────────────────────────────────

export default function LogisticsIndex({ incomingOrders, outgoingOrders }: LogisticsIndexProps) {
  const [layout, setLayout] = useState<'horizontal' | 'vertical'>('horizontal')

  const incomingColumns: DataTableColumn<ShipmentRow>[] = [
    {
      key: 'po_number', label: 'PO Number',
      render: row => (
        <Link href={`/inertia_customer_orders/${row.id}`} className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
          {row.po_number || `#${row.id}`}
        </Link>
      ),
      sortValue: row => row.po_number || '',
      filterValue: row => row.po_number || '',
    },
    {
      key: 'incoming_ship_date', label: 'Ship Date',
      render: row => <>{formatDate(row.incoming_ship_date)}</>,
      sortValue: row => row.incoming_ship_date || '',
      filterValue: row => row.incoming_ship_date || '',
    },
    {
      key: 'warehouse', label: 'Warehouse',
      render: row => <>{row.warehouse || '—'}</>,
      sortValue: row => row.warehouse || '',
      filterValue: row => row.warehouse || '',
    },
  ]

  const outgoingColumns: DataTableColumn<ShipmentRow>[] = [
    {
      key: 'po_number', label: 'PO Number',
      render: row => (
        <Link href={`/inertia_customer_orders/${row.id}`} className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
          {row.po_number || `#${row.id}`}
        </Link>
      ),
      sortValue: row => row.po_number || '',
      filterValue: row => row.po_number || '',
    },
    {
      key: 'ship_out_date', label: 'Ship Out Date',
      render: row => <>{formatDate(row.ship_out_date)}</>,
      sortValue: row => row.ship_out_date || '',
      filterValue: row => row.ship_out_date || '',
    },
    {
      key: 'warehouse', label: 'Warehouse',
      render: row => <>{row.warehouse || '—'}</>,
      sortValue: row => row.warehouse || '',
      filterValue: row => row.warehouse || '',
    },
  ]

  return (
    <>
      <Head title="Logistics" />

      <PageHeader title="Logistics" subtitle="Shipment Tracking" icon={<GlobeAltIcon className="h-5 w-5" />} actions={
        <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-700">
          <button onClick={() => setLayout('horizontal')} className={clsx('rounded-l-lg px-2 py-1', layout === 'horizontal' ? 'bg-indigo-500 text-white' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400')} title="Side by side">
            <Squares2X2Icon className="h-4 w-4" />
          </button>
          <button onClick={() => setLayout('vertical')} className={clsx('rounded-r-lg px-2 py-1', layout === 'vertical' ? 'bg-indigo-500 text-white' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400')} title="Stacked">
            <Bars3Icon className="h-4 w-4" />
          </button>
        </div>
      } />

      <div className={clsx(layout === 'horizontal' ? 'grid grid-cols-1 gap-6 lg:grid-cols-2' : 'space-y-8')}>
        {/* Incoming */}
        <section>
          <div className="mb-2 flex items-center gap-2">
            <ArrowDownIcon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">Incoming Shipments</h2>
            <Badge color="indigo"><span className="text-xs">{incomingOrders.length}</span></Badge>
          </div>
          <DataTable
            rows={incomingOrders}
            columns={incomingColumns}
            rowKey={row => row.id}
            emptyText="No incoming shipments"
            filteredEmptyText="No incoming shipments match your filters"
          />
        </section>

        {/* Outgoing */}
        <section>
          <div className="mb-2 flex items-center gap-2">
            <ArrowUpIcon className="h-4 w-4 text-emerald-500" />
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">Outgoing Shipments</h2>
            <Badge color="emerald"><span className="text-xs">{outgoingOrders.length}</span></Badge>
          </div>
          <DataTable
            rows={outgoingOrders}
            columns={outgoingColumns}
            rowKey={row => row.id}
            emptyText="No outgoing shipments"
            filteredEmptyText="No outgoing shipments match your filters"
          />
        </section>
      </div>
    </>
  )
}

LogisticsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
